import TemplateView from '../../TemplateView'
import {connect} from '../../redux/utils'
import {bindActionCreators} from 'redux'
import RequirementCheckbox from '../passwordSettings/components/RequirementCheckbox'
import messageResource from '../../messageResource'
import SettingsNumberInput from './components/SettingsNumberInput'
import * as Selectors from './selectors'
import * as Actions from './actions'
import DropdownDialogWithPointerWrapper from '../../components/DropdownDialogWithPointerWrapper'
import LockoutSettingsDetailsPopup from './components/LockoutSettingsDetailsPopup'
import Button from '../../components/Button'
import NoteWithIcon from '../passwordSettings/components/NoteWithIcon'
import {UNLOCK_ALL_STATUS} from './constants'

export class LockoutPolicyContainer extends TemplateView {
    constructor({
        useLockoutPolicy,
        duration,
        durationErrorMessage,
        threshold,
        thresholdErrorMessage,
        counter,
        counterErrorMessage,
        isModified,
        unlockAllStatus,

        setUseLockoutPolicy,
        setLockoutDuration,
        setLockoutThreshold,
        setLockoutCounter,
        resetAllLocks,
        onPolicyChanged,

        onModify = () => {},
        onSave = () => {},
    }) {
        super()

        this.state = {
            useLockoutPolicy,
            duration,
            durationErrorMessage,
            threshold,
            thresholdErrorMessage,
            counter,
            counterErrorMessage,
            isModified,
            unlockAllStatus,
        }

        this.actions = {
            setUseLockoutPolicy,
            setLockoutDuration,
            setLockoutThreshold,
            setLockoutCounter,
            resetAllLocks,
            onPolicyChanged,
        }

        this.callbacks = {
            onModify,
            onSave,
        }

        this.onStatePropertyChanged('useLockoutPolicy', useLockoutPolicy => {
            this.useLockoutCheckbox.setState({
                value: useLockoutPolicy,
                label: useLockoutPolicy ?
                       messageResource.getMessage('lockout_policy.use_full_text')
                                        :
                       messageResource.getMessage('lockout_policy.use'),
            })

            this.durationInput.setState({visible: useLockoutPolicy})
            this.thresholdInput.setState({visible: useLockoutPolicy})
            this.counterInput.setState({visible: useLockoutPolicy})
            this.$settingsNote.toggleClass('visible', useLockoutPolicy)
            this.$lockoutPolicyReset.toggleClass('visible', useLockoutPolicy)
        })

        this.onStatePropertyChanged('duration', value => this.durationInput.setState({value}))
        this.onStatePropertyChanged('threshold', value => this.thresholdInput.setState({value}))
        this.onStatePropertyChanged('counter', value => this.counterInput.setState({value}))

        this.onStatePropertyChanged('durationErrorMessage', invalidValueMessage => this.durationInput.setState({invalidValueMessage}))
        this.onStatePropertyChanged('thresholdErrorMessage', invalidValueMessage => this.thresholdInput.setState({invalidValueMessage}))
        this.onStatePropertyChanged('counterErrorMessage', invalidValueMessage => this.counterInput.setState({invalidValueMessage}))

        this.onAnyStatePropertyChanged(['useLockoutPolicy', 'duration', 'threshold', 'counter', 'isModified'], () => {
            if (this.state.isModified) {
                this.callbacks.onModify(!this.state.useLockoutPolicy || (!this.state.durationErrorMessage && !this.state.thresholdErrorMessage && !this.state.counterErrorMessage))
                this.actions.onPolicyChanged()
            }
        })

        this.onAnyStatePropertyChanged(['duration', 'threshold', 'counter', 'durationErrorMessage', 'thresholdErrorMessage', 'counterErrorMessage'], () => {

            let isNeedUpdateNote = this.state.duration !== '' && this.state.threshold !== '' && this.state.counter !== ''
                                   && !this.state.durationErrorMessage && !this.state.thresholdErrorMessage && !this.state.counterErrorMessage

            if (isNeedUpdateNote) {
                const text = this.state.threshold === 0
                             ? messageResource.getMessage('lockout_policy.settings.settings_note.no_threshold')
                             : messageResource.getMessage('lockout_policy.settings.settings_note.full', [this.state.duration,
                                                                                                         this.state.threshold,
                                                                                                         this.state.counter])

                this.note.setState({text})
            }
        })

        this.onStatePropertyChanged('unlockAllStatus', (unlockAllStatus) => {
            switch (unlockAllStatus) {
                case UNLOCK_ALL_STATUS.INITIAL:
                    this.resetNote.note(messageResource.getMessage('lockout_policy.reset.note'))
                    this.resetButton.setState({disabled: false})
                    break
                case UNLOCK_ALL_STATUS.IN_PROGRESS:
                    this.resetNote.progress(messageResource.getMessage('lockout_policy.reset.note.in_progress'))
                    this.resetButton.setState({disabled: true})
                    break
                case UNLOCK_ALL_STATUS.SUCCESS:
                    this.resetNote.success(messageResource.getMessage('lockout_policy.reset.note.success'))
                    this.resetButton.setState({disabled: false})
                    break
            }
        })
    }

    templateHtml() {
        return `
            <div class='lockoutPolicy_container'>
                <div class='lockoutPolicy_controls'>
                    <div class='useLockoutCheckbox'/>
                    <div class='settingsContainer'>
                        <div class='settings'/>
                        <div class='settingsNote'>
                            <div class='settingsNoteWithIcon'/>
                            <div class='detailsButton dashedLink'><%=detailsButtonText%></div>
                        </div>
                    </div>
                </div>
                <div class='lockoutPolicy_reset'>
                    <div class='resetButtonContainer'/>
                    <div class='resetNote'/>
                </div>
            </div>
`
    }

    messageKeys() {
        return {
            detailsButtonText: 'lockout_policy.settings.settings_note.details_button'
        }
    }

    afterRender() {
        const $controlsBlock = this.$el.find('.useLockoutCheckbox')
        this.useLockoutCheckbox = new RequirementCheckbox({
            label: messageResource.getMessage('lockout_policy.use'),
            onValueChange: (checked) => this.actions.setUseLockoutPolicy(checked)
        }).init($controlsBlock)

        const $settings = this.$el.find('.settings')

        this.durationInput = new SettingsNumberInput({
            additionalClass: 'settingsInput',
            title: messageResource.getMessage('lockout_policy.settings.duration'),
            units: messageResource.getMessage('lockout_policy.settings.duration.units'),
            maxLength: 4,
            onChange: (duration) => this.actions.setLockoutDuration(duration),
        })
        this.durationInput.init($settings)

        this.thresholdInput = new SettingsNumberInput({
            additionalClass: 'settingsInput',
            title: messageResource.getMessage('lockout_policy.settings.threshold'),
            units: messageResource.getMessage('lockout_policy.settings.threshold.units'),
            maxLength: 3,
            onChange: (threshold) => {this.actions.setLockoutThreshold(threshold)},
        })
        this.thresholdInput.init($settings)

        this.counterInput = new SettingsNumberInput({
            additionalClass: 'settingsInput',
            title: messageResource.getMessage('lockout_policy.settings.reset_counter_after'),
            units: messageResource.getMessage('lockout_policy.settings.reset_counter_after.units'),
            maxLength: 4,
            onChange: (counter) => {this.actions.setLockoutCounter(counter)},
        })
        this.counterInput.init($settings)

        this.$settingsNote = this.$el.find('.settingsNote')

        this.note = new NoteWithIcon().init(this.$settingsNote.find('.settingsNoteWithIcon'))

        const $settingsNoteDetails = this.$settingsNote.find('.detailsButton')
        this.detailsPopup = new DropdownDialogWithPointerWrapper({
            additionalClass: 'detailsPopup',
            dialogOffsets: {left: -70, top: -10},
            dialog: new LockoutSettingsDetailsPopup(),
            toggleMode: true,
        }).init(this.getEl())
        $settingsNoteDetails.click(() => this.detailsPopup.showAt($settingsNoteDetails, {my: 'left bottom', at: 'left top'}))

        this.resetButton = new Button({
            label: messageResource.getMessage('lockout_policy.reset.button_label'),
            additionalClass: 'greyButton resetButton',
            onClickCb: () => this.state.useLockoutPolicy && this.actions.resetAllLocks(),

        }).init(this.$el.find('.resetButtonContainer'))

        this.resetNote = new NoteWithIcon().init(this.find('.resetNote'))
        this.$lockoutPolicyReset = this.find('.lockoutPolicy_reset')
    }
}

export const LockoutPolicyContainerConnected = connect(
    (state) => ({
        useLockoutPolicy: Selectors.isUseLockoutPolicy(state),
        duration: Selectors.getDuration(state),
        durationErrorMessage: Selectors.getDurationErrorMessage(state),
        threshold: Selectors.getThreshold(state),
        thresholdErrorMessage: Selectors.getThresholdErrorMessage(state),
        counter: Selectors.getCounter(state),
        counterErrorMessage: Selectors.getCounterErrorMessage(state),
        isModified: Selectors.isModified(state),
        unlockAllStatus: Selectors.getUnlockAllStatus(state),
    }),
    (dispatch) => ({
        setUseLockoutPolicy: bindActionCreators(Actions.setUseLockoutPolicy, dispatch),
        setLockoutDuration: bindActionCreators(Actions.setLockoutDuration, dispatch),
        setLockoutThreshold: bindActionCreators(Actions.setLockoutThreshold, dispatch),
        setLockoutCounter: bindActionCreators(Actions.setLockoutCounter, dispatch),
        resetAllLocks: bindActionCreators(Actions.resetAllLocks, dispatch),
        setUnlockStatus: bindActionCreators(Actions.setUnlockAllStatus, dispatch),
        onPolicyChanged: bindActionCreators(Actions.onPolicyChanged, dispatch),
    })
)(LockoutPolicyContainer)



// WEBPACK FOOTER //
// ./web/js/administration/lockoutPolicy/LockoutPolicyContainer.js